/**
 * Page 1 — Cover Page (v8 mock parity, refined).
 *
 *   ┌────────────────────────────────────────────────┐
 *   │ NAVY BAND (top ~50%) with diagonal cut         │
 *   │   ── gold rule                                 │
 *   │   CYBERSECURITY RISK ASSESSMENT  (gold eyebrow)│
 *   │   <Client Organization>          (large white) │
 *   │   Parent · Sector / HQ                         │
 *   ├──── gold hairline separator ───────────────────┤
 *   │                                                │
 *   │     [DELIVERED BY]        [PREPARED FOR]       │
 *   │     [CSV PRO LOGO]        [CLIENT LOGO]        │
 *   │                                                │
 *   │            April 26, 2026   (gen date)         │
 *   │                                                │
 *   │   ──────── footer rule ─────────────────────── │
 *   │   CSV PRO · CYBERSECURITY...     Confidential  │
 *   └────────────────────────────────────────────────┘
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, formatLongDate } from "./theme";
import { type LoadedLogo } from "./loadLogo";

export interface CoverPageContext {
  clientOrgName: string;
  parentOrgName?: string | null;
  sector?: string | null;
  headquarters?: string | null;
  engagementStart?: string | null;
  engagementEnd?: string | null;
  conductedBy?: string | null;
  methodology?: string | null;
  format?: string | null;
  reportDateIso?: string;
  csvProLogo: LoadedLogo | null;
  clientLogo: LoadedLogo | null;
}

const TARGET_LOGO_H = 60;
const MAX_LOGO_W = 200;

export function renderCoverPage(doc: jsPDF, ctx: CoverPageContext): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // ── Navy band with diagonal cut (top ~50%) ──
  const bandH = pageH * 0.50;
  doc.setFillColor(...REPORT_THEME.navy.rgb);
  doc.rect(0, 0, pageW, bandH, "F");

  // Diagonal lighter-navy overlay (subtle depth)
  doc.setFillColor(...REPORT_THEME.accent.rgb);
  doc.triangle(
    0, bandH * 0.55,
    pageW, bandH * 0.30,
    pageW, bandH,
    "F",
  );
  doc.setFillColor(...REPORT_THEME.navy.rgb);
  doc.rect(0, bandH - 6, pageW, 6, "F");

  // Gold eyebrow rule
  const eyebrowY = 168;
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(2.2);
  doc.line(PAGE_MARGIN, eyebrowY, PAGE_MARGIN + 70, eyebrowY);

  // Eyebrow label
  doc.setTextColor(...REPORT_THEME.gold.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("CYBERSECURITY RISK ASSESSMENT", PAGE_MARGIN, eyebrowY + 18);

  // Client title
  doc.setTextColor(...REPORT_THEME.textOnNavy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(34);
  doc.text(ctx.clientOrgName || "Client Organization", PAGE_MARGIN, eyebrowY + 60);

  // Subtitle (parent · sector / location)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  const subParts: string[] = [];
  if (ctx.parentOrgName) subParts.push(`Parent: ${ctx.parentOrgName}`);
  if (ctx.sector) subParts.push(ctx.sector);
  if (subParts.length) {
    doc.text(subParts.join("  ·  "), PAGE_MARGIN, eyebrowY + 84);
  }
  if (ctx.headquarters) {
    doc.text(ctx.headquarters, PAGE_MARGIN, eyebrowY + 102);
  }

  // Gold separator at base of band
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.4);
  doc.line(0, bandH + 1, pageW, bandH + 1);

  // ── Mid-page dual logo block (tightened, with vertical divider) ──
  // Move higher: sit just below the navy band's separator with a comfortable gap.
  const blockTop = bandH + 56;          // top of "DELIVERED BY" / "PREPARED FOR" labels
  const labelToLogoGap = 14;
  const logoBaseline = blockTop + labelToLogoGap + TARGET_LOGO_H;

  // Bring the two halves CLOSER to the page center so they read as one unit.
  const innerOffset = pageW * 0.18;     // distance from center axis
  const leftHalfCx = pageW / 2 - innerOffset;
  const rightHalfCx = pageW / 2 + innerOffset;

  // Labels above logos
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const leftLabel = "DELIVERED BY";
  const rightLabel = "PREPARED FOR";
  doc.text(leftLabel, leftHalfCx - doc.getTextWidth(leftLabel) / 2, blockTop);
  doc.text(rightLabel, rightHalfCx - doc.getTextWidth(rightLabel) / 2, blockTop);

  // Tiny gold underline beneath each label
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.2);
  doc.line(leftHalfCx - 18, blockTop + 4, leftHalfCx + 18, blockTop + 4);
  doc.line(rightHalfCx - 18, blockTop + 4, rightHalfCx + 18, blockTop + 4);

  // Logos centered under each label, sharing baseline.
  // Normalize by height: client = 100%, CSV Pro = 85% so the client logo
  // reads as the dominant identity on the cover.
  const clientLogoH = TARGET_LOGO_H;
  const csvProLogoH = TARGET_LOGO_H * 0.85;
  drawCenteredLogo(doc, ctx.csvProLogo, leftHalfCx, logoBaseline, "CSV Pro", "csv pro logo cover", csvProLogoH);
  drawCenteredLogo(doc, ctx.clientLogo, rightHalfCx, logoBaseline, ctx.clientOrgName, "client logo cover", clientLogoH);

  // Thin vertical divider between the two blocks
  const dividerTop = blockTop - 6;
  const dividerBottom = logoBaseline + 8;
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.6);
  doc.line(pageW / 2, dividerTop, pageW / 2, dividerBottom);

  // ── Generation date below logos ──
  const dateText = formatLongDate(ctx.reportDateIso ?? new Date().toISOString());
  const dateY = logoBaseline + 56;
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const dateLbl = "REPORT DATE";
  doc.text(dateLbl, pageW / 2 - doc.getTextWidth(dateLbl) / 2, dateY);
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1);
  doc.line(pageW / 2 - 20, dateY + 4, pageW / 2 + 20, dateY + 4);

  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(dateText, pageW / 2 - doc.getTextWidth(dateText) / 2, dateY + 24);

  // ── Footer rule + caption ──
  const footerRuleY = pageH - 44;
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.6);
  doc.line(PAGE_MARGIN, footerRuleY, pageW - PAGE_MARGIN, footerRuleY);

  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const footerClient = (ctx.clientOrgName || "Client").toUpperCase();
  doc.text(`${footerClient}  ·  CYBERSECURITY RISK ASSESSMENT REPORT`, PAGE_MARGIN, footerRuleY + 14);
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "normal");
  const conf = "Confidential";
  doc.text(conf, pageW - PAGE_MARGIN - doc.getTextWidth(conf), footerRuleY + 14);
}


function drawCenteredLogo(
  doc: jsPDF,
  logo: LoadedLogo | null,
  centerX: number,
  baselineY: number,
  fallbackText: string,
  debugLabel: string,
  targetHeight: number = TARGET_LOGO_H,
): void {
  if (!logo) {
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    const t = fallbackText.length > 28 ? fallbackText.slice(0, 28) + "…" : fallbackText;
    const w = doc.getTextWidth(t);
    doc.text(t, centerX - w / 2, baselineY);
    return;
  }
  let h = targetHeight;
  let w = (logo.width / logo.height) * h;
  if (w > MAX_LOGO_W) {
    w = MAX_LOGO_W;
    h = (logo.height / logo.width) * w;
  }
  const x = centerX - w / 2;
  const y = baselineY - h;
  try {
    doc.addImage(logo.dataUrl, logo.format, x, y, w, h, undefined, "FAST");
    console.info(`[reviewer-pdf] ${debugLabel}: doc.addImage() executes`, { x, y, w, h });
  } catch (e) {
    console.warn(`[reviewer-pdf] ${debugLabel}: jsPDF addImage failed`, { e });
    if (fallbackText !== "CSV Pro") {
      drawInitialsFallback(doc, centerX, baselineY - TARGET_LOGO_H / 2, fallbackText);
    }
  }
}

function drawInitialsFallback(doc: jsPDF, centerX: number, centerY: number, name: string): void {
  const initials = (name || "Client")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "CL";
  doc.setFillColor(...REPORT_THEME.navy.rgb);
  doc.circle(centerX, centerY, 24, "F");
  doc.setTextColor(...REPORT_THEME.textOnNavy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(initials, centerX - doc.getTextWidth(initials) / 2, centerY + 4);
}
